import React, {
  FormEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { navigate } from 'gatsby';

import { MultiformProps } from '~/components/blocks/multiform';
import PhoneCodes from '~/components/blocks/partials/phone_codes';

import countries from '~/data/sidebar/countries.json';
import { ABUSE_TYPES, ABUSE_URL } from './constants';
import Submit from '~/components/fragments/submit';
import { blindPostRequest } from '~/utils/network';
import { CapCaptcha } from '~/components/reusable/CapCaptcha';

function AbuseForm({ block, includeAbuseInUrl = true }: MultiformProps) {
  const getInitialAbuseType = (): keyof typeof ABUSE_TYPES => {
    if (typeof window === 'undefined')
      return ABUSE_TYPES.CopyrightDmca?.value as keyof typeof ABUSE_TYPES;
    const searchParams = new URLSearchParams(window.location.search);
    const report = searchParams.get('report')?.toLowerCase() || '';

    const matchedKey = Object.keys(ABUSE_TYPES).find(
      key => key.toLowerCase() === report
    );

    return (
      (matchedKey as keyof typeof ABUSE_TYPES) ??
      ABUSE_TYPES.CopyrightDmca?.value
    );
  };

  const [abuseType, setAbuseType] =
    useState<keyof typeof ABUSE_TYPES>(getInitialAbuseType);
  const [selectedCountry, setSelectedCountry] = useState(1);
  const [emailError, setEmailError] = useState('');
  const [errorData, setErrorData] = useState<{
    success: boolean;
    message: string;
    missingFields: string[];
  } | null>(null);

  const formRef = useRef(null);

  // Set from popstate
  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const report = searchParams.get('report')?.toLowerCase() || '';

    const matchedKey = Object.keys(ABUSE_TYPES).find(
      key => key.toLowerCase() === report
    );

    if (matchedKey) {
      setAbuseType(matchedKey as keyof typeof ABUSE_TYPES);
    } else {
      // Optional fallback: set default if report is missing or invalid
      setAbuseType(
        ABUSE_TYPES.CopyrightDmca?.value as keyof typeof ABUSE_TYPES
      ); // or your default abuse type
    }
  }, []);

  // Track back/forward and update state
  useEffect(() => {
    const handlePopState = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const report = searchParams.get('report')?.toLowerCase() || '';

      const matchedKey = Object.keys(ABUSE_TYPES).find(
        key => key.toLowerCase() === report
      );

      if (matchedKey) {
        setAbuseType(matchedKey as keyof typeof ABUSE_TYPES);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL only when needed
  useEffect(() => {
    if (!abuseType) return;

    const params = new URLSearchParams(window.location.search);
    const current = params.get('report');

    if (current?.toLowerCase() !== abuseType.toLowerCase()) {
      params.set('report', abuseType);
      if (includeAbuseInUrl) params.set('type', 'abuse');

      const newUrl =
        window.location.pathname +
        '?' +
        params.toString() +
        window.location.hash;

      window.history.pushState({}, '', newUrl);
    }
  }, [abuseType]);

  const handlePhoneCode = (phoneCode: number) => {
    setSelectedCountry(phoneCode);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const contactEmail = `${formData.get('contactEmail')}`;
    const confirmEmail = `${formData.get('confirmEmail')}`;

    if (contactEmail !== confirmEmail) {
      setEmailError('Emails should match.');
      return;
    } else setEmailError('');

    formData.append(
      'fullName',
      formData.get('firstName') + ' ' + formData.get('lastName')
    );
    formData.delete('firstName');
    formData.delete('lastName');
    formData.delete('confirmEmail');

    const phoneData = formData.get('phone');
    if (phoneData) {
      formData.delete('phone');
      formData.append('phone', '+' + selectedCountry + ' ' + phoneData);
    }

    formData.append('abuseType', abuseType);

    if (!formData.get('PowToken')) {
      setErrorData({
        success: false,
        message: 'Please verify that you are human.',
        missingFields: [],
      });
      return;
    }

    const parseTextareaToArray = (value: FormDataEntryValue | null) =>
      typeof value === 'string'
        ? value
            .replace(/(https?:\/\/)/g, '\n$1')
            .trim()
            .split('\n')
            .map(line => line.trim())
            .filter(line => line !== '')
        : undefined;

    // Process infringingUrls only if it exists and is non-empty
    const infringingUrls = parseTextareaToArray(formData.get('infringingUrls'));
    if (infringingUrls) {
      formData.delete('infringingUrls');
      formData.append('infringingUrls', JSON.stringify(infringingUrls));
    }

    // Process sourceIps only if it exists and is non-empty
    const sourceIps = parseTextareaToArray(formData.get('sourceIps'));
    if (sourceIps) {
      formData.delete('sourceIps');
      formData.append('sourceIps', JSON.stringify(sourceIps));
    }

    const destinationIps = parseTextareaToArray(formData.get('destinationIps'));
    if (destinationIps) {
      formData.delete('destinationIps');
      formData.append('destinationIps', JSON.stringify(destinationIps));
    }

    const destinationPortsAndProtocols = parseTextareaToArray(
      formData.get('destinationPortsAndProtocols')
    );
    if (destinationPortsAndProtocols) {
      formData.delete('destinationPortsAndProtocols');
      formData.append(
        'destinationPortsAndProtocols',
        JSON.stringify(destinationPortsAndProtocols)
      );
    }

    const ackChecked = formData.get('acknowledgment512f') !== null;
    const dsaChecked = formData.get('dsaCertification') !== null;

    if (ackChecked) formData.set('acknowledgment512f', 'true');
    if (dsaChecked) formData.set('dsaCertification', 'true');

    const TO_BE_PARSED = [
      'acknowledgment512f',
      'dsaCertification',
      'infringingUrls',
      'sourceIps',
      'destinationIps',
      'destinationPortsAndProtocols',
    ];

    // Convert FormData to plain object
    const formBody: Record<string, any> = {};
    for (const [key, value] of formData.entries()) {
      try {
        if (TO_BE_PARSED.includes(key)) {
          formBody[key] = JSON.parse(value as string);
        } else {
          formBody[key] = value;
        }
      } catch {
        formBody[key] = value;
      }
    }
    blindPostRequest(ABUSE_URL, formBody, true).then(async data => {
      // Normalize response to a common shape
      const isBoolean = typeof data === 'boolean';

      if (isBoolean && data === true) {
        setErrorData(null);
        navigate('/thank-you');
      } else if (!isBoolean && data?.success === true) {
        setErrorData(null);
        navigate('/thank-you');
      } else {
        // Something went wrong — set error data accordingly
        const errorPayload = isBoolean
          ? { success: false, message: 'Submission failed' }
          : data;

        if (errorPayload?.missingFields) {
          errorPayload.missingFields = errorPayload.missingFields.map(
            (field: string) =>
              typeof field === 'string' ? field.toLowerCase() : field
          );
        }
        setErrorData(errorPayload);
      }
    });
  };

  return (
    <form
      className='multiform__form contact-form label-bold-form'
      onSubmit={handleSubmit}
      ref={formRef}
    >
      {block?.side_image && (
        <img
          src={block.side_image}
          alt={block.side_image_alt}
          title={block.side_image_alt}
          className='multiform__image'
        />
      )}

      <div className='form-item form-select col-span-full'>
        <label htmlFor='abuseType'>Type of report*</label>
        <select
          name='abuseType'
          value={abuseType}
          onChange={e => setAbuseType(e.target.value)}
          required
        >
          {Object.values(ABUSE_TYPES).map(type => (
            <option
              key={type.value}
              value={type.value}
            >
              {type.label}
            </option>
          ))}
        </select>
      </div>
      {ABUSE_TYPES[abuseType]?.fields['firstName'] && (
        <div className='form-item'>
          <label htmlFor='firstName'>First Name*</label>
          <input
            type='text'
            name='firstName'
            aria-label='First Name'
            id='firstName'
            required
            placeholder='Your first name'
            style={
              errorData?.missingFields?.includes('fullname')
                ? { border: '1px solid red' }
                : {}
            }
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['lastName'] && (
        <div className='form-item'>
          <label htmlFor='lastName'>Last Name*</label>
          <input
            type='text'
            name='lastName'
            autoComplete='last-name'
            id='lastName'
            required
            placeholder='Your last name'
            style={
              errorData?.missingFields?.includes('fullname')
                ? { border: '1px solid red' }
                : {}
            }
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['copyrightHolderName'] && (
        <div>
          <div className='form-item'>
            <label htmlFor='copyrightHolderName'>
              Copyright Holders Full Name*
            </label>
            <input
              type='text'
              id='copyrightHolderName'
              name='copyrightHolderName'
              required
              placeholder='Your name'
              style={
                errorData?.missingFields?.includes('copyrightholdername')
                  ? { border: '1px solid red' }
                  : {}
              }
            />
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['contactEmail'] && (
        <>
          <div className='form-item'>
            <label htmlFor='contactEmail'>Email*</label>
            <input
              type='email'
              id='contactEmail'
              name='contactEmail'
              required
              placeholder='you@company.com'
              style={emailError ? { border: '1px solid red' } : {}}
            />
            {emailError && (
              <div className='error-message !mb-0 mt-2'>{emailError}</div>
            )}
          </div>
          <div className='form-item'>
            <label htmlFor='confirmEmail'>Confirm Email*</label>
            <input
              type='email'
              id='confirmEmail'
              name='confirmEmail'
              required
              placeholder='you@company.com'
              style={emailError ? { border: '1px solid red' } : {}}
            />
          </div>
        </>
      )}
      {ABUSE_TYPES[abuseType]?.fields['phone'] && (
        <div className='form-item'>
          <label htmlFor='phone'>Telephone</label>
          <div className='phone-wr'>
            <PhoneCodes setPhoneCode={handlePhoneCode} />
            <input
              type='text'
              name='phone'
              placeholder='xxx-xxx-xxxx'
            />
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['companyName'] && (
        <div className='form-item'>
          <label htmlFor='companyName'>Company Name</label>
          <input
            type='text'
            id='companyName'
            name='companyName'
            placeholder='Company name'
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['trademarkedWordOrSymbol'] && (
        <div>
          <div className='form-item'>
            <label htmlFor='trademarkedWordOrSymbol'>Trademark*</label>
            <input
              type='text'
              id='trademarkedWordOrSymbol'
              name='trademarkedWordOrSymbol'
              placeholder='Trademark word or symbol'
              required
              style={
                errorData?.missingFields?.includes('trademarkedwordorsymbol')
                  ? { border: '1px solid red' }
                  : {}
              }
            />
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['registrationNumber'] && (
        <div className='form-item'>
          <label htmlFor='registrationNumber'>Registration Number*</label>
          <input
            type='text'
            id='registrationNumber'
            name='registrationNumber'
            placeholder='Registration number'
            required
            style={
              errorData?.missingFields?.includes('registrationnumber')
                ? { border: '1px solid red' }
                : {}
            }
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['registrationOffice'] && (
        <div className='form-item col-span-full'>
          <label htmlFor='registrationOffice'>Registration Office*</label>
          <input
            type='text'
            id='registrationOffice'
            name='registrationOffice'
            placeholder='Registration office'
            required
            style={
              errorData?.missingFields?.includes('registrationoffice')
                ? { border: '1px solid red' }
                : {}
            }
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['address'] && (
        <div>
          <div className='form-item'>
            <label htmlFor='address'>Address*</label>
            <input
              type='text'
              id='address'
              name='address'
              placeholder='Your address'
              required
              style={
                errorData?.missingFields?.includes('address')
                  ? { border: '1px solid red' }
                  : {}
              }
            />
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['city'] && (
        <div className='form-item'>
          <label htmlFor='city'>City*</label>
          <input
            type='text'
            id='city'
            required
            name='city'
            placeholder='City'
            style={
              errorData?.missingFields?.includes('city')
                ? { border: '1px solid red' }
                : {}
            }
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['stateOrProvince'] && (
        <div className='form-item'>
          <label htmlFor='stateOrProvince'>State/Province*</label>
          <input
            type='text'
            id='stateOrProvince'
            name='stateOrProvince'
            required
            placeholder='State'
            style={
              errorData?.missingFields?.includes('stateorprovince')
                ? { border: '1px solid red' }
                : {}
            }
          />
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['country'] && (
        <div className='col-span-full'>
          <div className='form-item form-select'>
            <label htmlFor='country'>Country</label>
            <select
              name='country'
              required
              defaultValue={'United States'}
              style={
                errorData?.missingFields?.includes('country')
                  ? { border: '1px solid red' }
                  : {}
              }
            >
              {countries.list.map(country => (
                <option
                  key={country.name}
                  value={country.name}
                >
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['infringingUrls'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='infringingUrls'>Infringing Urls*</label>
            <textarea
              name='infringingUrls'
              id='infringingUrls'
              minLength={10}
              placeholder='Separate multiple URLs with a new line'
              style={
                errorData?.missingFields?.includes('infringingurls')
                  ? { border: '1px solid red' }
                  : {}
              }
            ></textarea>
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['evidenceOfAbuse'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='evidenceOfAbuse'>Evidence of Abuse*</label>
            <textarea
              name='evidenceOfAbuse'
              id='evidenceOfAbuse'
              minLength={10}
              placeholder='Evidence of abuse'
              required
              style={
                errorData?.missingFields?.includes('evidenceofabuse')
                  ? { border: '1px solid red' }
                  : {}
              }
            ></textarea>
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['sourceIps'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='sourceIps'>Source IPs</label>
            <textarea
              name='sourceIps'
              id='sourceIps'
              minLength={10}
              placeholder='Separate multiple IPs with a new line'
            ></textarea>
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['destinationIps'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='destinationIps'>Destination IPs</label>
            <textarea
              name='destinationIps'
              id='destinationIps'
              minLength={10}
              placeholder='Separate multiple IPs with a new line'
            ></textarea>
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['destinationPortsAndProtocols'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='destinationPortsAndProtocols'>
              Destination ports and protocols
            </label>
            <input
              type='text'
              id='destinationPortsAndProtocols'
              name='destinationPortsAndProtocols'
              placeholder='e.g. 443/TCP'
            />
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['comments'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='comments'>Comments</label>
            <textarea
              name='comments'
              id='comments'
              placeholder='Additional comments'
            ></textarea>
          </div>
        </div>
      )}

      {ABUSE_TYPES[abuseType]?.fields['originalWorkDescription'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='originalWorkDescription'>
              Describe the original work*
            </label>
            <textarea
              name='originalWorkDescription'
              id='originalWorkDescription'
              minLength={10}
              placeholder='Describe the details'
              required
              style={
                errorData?.missingFields?.includes('originalworkdescription')
                  ? { border: '1px solid red' }
                  : {}
              }
            ></textarea>
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['digitalSignature'] && (
        <div className='col-span-full'>
          <div className='form-item'>
            <label htmlFor='digitalSignature'>Digital Signature</label>
            <p className='mb-4 text-[15px]'>
              By entering your name, you affirm all information is true and
              accurate while agreeing to the policies on this page.
            </p>
            <input
              type='text'
              id='digitalSignature'
              name='digitalSignature'
              required
              placeholder='Enter your name to sign this submission'
              style={
                errorData?.missingFields?.includes('digitalsignature')
                  ? { border: '1px solid red' }
                  : {}
              }
            />
          </div>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['acknowledgment512f'] && (
        <div className='col-span-full'>
          <div className='form-terms !mb-0'>
            <label>
              <input
                type='checkbox'
                name='acknowledgment512f'
                required
                style={
                  errorData?.missingFields?.includes('acknowledgment512f')
                    ? { outline: '1px solid red' }
                    : {}
                }
              />
              512(f) acknowledgment, Good faith belief, Authority to act*
            </label>
          </div>
          <p className='mb-4 text-[15px]'>
            By checking this box, you attest, under penalty of perjury, that you
            have a good faith belief that use of the material in this report is
            not authorized by the copyright owner, its agent, or the law; AND
            you are authorized to act on behalf of the copyright owner; AND you
            understand, under 17 U.S.C. § 512(f), you may be liable for any
            damages, including costs and attorneys' fees, if you knowingly
            materially misrepresent reported material. Checking this box also
            confirms that you have a bona fide belief that the information and
            allegations contained in this report are accurate and complete, for
            purposes of the Digital Services Act.
          </p>
        </div>
      )}
      {ABUSE_TYPES[abuseType]?.fields['dsaCertification'] && (
        <div className='col-span-full'>
          <div className='form-terms !mb-0'>
            <label>
              <input
                type='checkbox'
                name='dsaCertification'
                required
                style={
                  errorData?.missingFields?.includes('dsacertification')
                    ? { outline: '1px solid red' }
                    : {}
                }
              />
              Declaration of Accuracy and Authority*
            </label>
          </div>
          {abuseType === 'TrademarkInfringement' ? (
            <p className='mb-4 text-[15px]'>
              By checking this box, you affirm that you have a good faith belief
              that the use of the trademark described above is not authorized by
              the trademark owner, its agent, or the law; that the information
              in this notice is accurate; and that you are the trademark owner
              or are authorized to act on behalf of the owner of an exclusive
              right that is allegedly infringed. You further certify, for
              purposes of the Digital Services Act, that the information and
              allegations contained in this report are accurate and complete to
              the best of your knowledge.
            </p>
          ) : abuseType === 'NCSEI' ? (
            <p className='mb-4 text-[15px]'>
              By checking this box, you affirm that the material described in
              this report consists of nude and/or sexually explicit images or
              videos of you, or of someone you are legally authorized to
              represent, that were shared without consent. You confirm that
              neither you nor the person you represent authorized the
              distribution of this content, that you have a good faith belief
              that the material is unlawful under applicable law, and that the
              information provided in this report is accurate and complete to
              the best of your knowledge. This submission constitutes a formal
              notice under the Digital Services Act.
            </p>
          ) : (
            <p className='mb-4 text-[15px]'>
              By checking this box, you affirm that you have a good faith belief
              that the material described in this report is unlawful under
              applicable law. You certify that the information provided is
              accurate and complete to the best of your knowledge, and that this
              submission constitutes a formal notice under the Digital Services
              Act.
            </p>
          )}
        </div>
      )}
      <CapCaptcha className='multiform-item--span mx-auto' />
      <Submit wrapperStyle='multiform-item--span'>Submit</Submit>
      {errorData?.message && (
        <div className='error-message !mb-0 mt-2'>{errorData.message}</div>
      )}
      {emailError && (
        <div className='error-message !mb-0 mt-2'>{emailError}</div>
      )}
    </form>
  );
}

export default AbuseForm;
